import React, { useState } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Button, TextField,
    Divider, Alert, IconButton, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import API_BASE_URL from '../config/api';

export default function SaaSLandingView({ onSelectTenant, onAccessDeveloper }) {
    // Search Subdomain State
    const [searchSubdomain, setSearchSubdomain] = useState('');
    const [searchError, setSearchError] = useState('');

    // Signup Modal State
    const [signupOpen, setSignupOpen] = useState(false);
    const [signupForm, setSignupForm] = useState({
        name: '',
        subdomain: '',
        owner_name: '',
        owner_email: '',
        owner_password: ''
    });
    const [signupResult, setSignupResult] = useState(null);
    const [signupLoading, setSignupLoading] = useState(false);


    // Handle Subdomain redirection
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setSearchError('');
        const trimmed = searchSubdomain.trim().toLowerCase();
        if (!trimmed) {
            setSearchError('कृपया तुमचे स्टोअर नाव किंवा सबडोमेन टाका.');
            return;
        }
        onSelectTenant(trimmed);
    };

    // Handle Signup Submit
    const handleSignupSubmit = async (e) => {
        e.preventDefault();
        setSignupLoading(true);
        setSignupResult(null);

        try {
            const response = await fetch(`${API_BASE_URL}/landlord/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(signupForm)
            });
            const data = await response.json();
            if (data.success) {
                setSignupResult({
                    success: true,
                    message: `Congratulations! Your store "${signupForm.name}" has been created successfully.`,
                    subdomain: signupForm.subdomain
                });
                setSignupForm({ name: '', subdomain: '', owner_name: '', owner_email: '', owner_password: '' });
            } else {
                setSignupResult({
                    success: false,
                    message: data.error || 'Failed to create your store. Subdomain might be taken.'
                });
            }
        } catch (error) {
            setSignupResult({
                success: false,
                message: 'Failed to connect to SaaS backend services. Please ensure Laravel is running.'
            });
        } finally {
            setSignupLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>

            {/* Curated Hero Section with Solid Dinexa Forest Green Background */}
            <Box sx={{ width: '100%', bgcolor: '#008751', py: 5, px: { xs: 2, md: 4 }, textAlign: 'center' }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto', mb: 4 }}>
                    <Typography variant="h3" sx={{ fontWeight: 950, color: 'white', mb: 1, fontSize: { xs: '1.8rem', md: '2.8rem' } }}>
                        Dinexa Restaurant Billing Softwares
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', maxW: 600, mx: 'auto', fontWeight: 600 }}>
                        One single platform built to accelerate counters, KDS, tables ordering, and snack spots.
                    </Typography>
                </Box>

                {/* Overlapping Slanted Columns Container */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        width: '100%',
                        height: { xs: 'auto', md: '460px' },
                        overflow: 'hidden',
                        borderRadius: 4,
                        border: '1px solid rgba(255,255,255,0.2)',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)',
                        gap: { xs: 2, md: 0 },
                        bgcolor: '#006f42'
                    }}
                >
                    {/* Panel 1: Food Court Token System / KDS */}
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            height: { xs: '200px', md: '100%' },
                            backgroundImage: 'linear-gradient(to bottom, rgba(0,111,66,0.2), rgba(0,111,66,0.9)), url("/kds_token_display.png")',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transform: { xs: 'none', md: 'skewX(-6deg)' },
                            marginLeft: { xs: 0, md: '-10px' },
                            marginRight: { xs: 0, md: '-10px' },
                            transition: 'all 0.4s ease',
                            overflow: 'hidden',
                            '&:hover': {
                                flex: { xs: 1, md: 1.3 }
                            }
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                left: { xs: 20, md: 35 },
                                right: 20,
                                transform: { xs: 'none', md: 'skewX(6deg)' }
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900, color: 'white' }}>
                                Token & KDS System
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', mt: 0.5, fontWeight: 600 }}>
                                Food Court ready screens & kitchen automation.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Panel 2: Software Screen Happy Operator */}
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            height: { xs: '200px', md: '100%' },
                            backgroundImage: 'linear-gradient(to bottom, rgba(0,111,66,0.2), rgba(0,111,66,0.9)), url("/pos_counter_printer.png")',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transform: { xs: 'none', md: 'skewX(-6deg)' },
                            marginLeft: { xs: 0, md: '-10px' },
                            marginRight: { xs: 0, md: '-10px' },
                            transition: 'all 0.4s ease',
                            overflow: 'hidden',
                            '&:hover': {
                                flex: { xs: 1, md: 1.3 }
                            }
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                left: { xs: 20, md: 35 },
                                right: 20,
                                transform: { xs: 'none', md: 'skewX(6deg)' }
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900, color: 'white' }}>
                                Billing Counter POS
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', mt: 0.5, fontWeight: 600 }}>
                                Professional counter cashier screen.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Panel 3: Waiter on Tablet */}
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            height: { xs: '200px', md: '100%' },
                            backgroundImage: 'linear-gradient(to bottom, rgba(0,111,66,0.2), rgba(0,111,66,0.9)), url("/software_screen_mobile.png")',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transform: { xs: 'none', md: 'skewX(-6deg)' },
                            marginLeft: { xs: 0, md: '-10px' },
                            marginRight: { xs: 0, md: '-10px' },
                            transition: 'all 0.4s ease',
                            overflow: 'hidden',
                            '&:hover': {
                                flex: { xs: 1, md: 1.3 }
                            }
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                left: { xs: 20, md: 35 },
                                right: 20,
                                transform: { xs: 'none', md: 'skewX(6deg)' }
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900, color: 'white' }}>
                                Captain Tablet App
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', mt: 0.5, fontWeight: 600 }}>
                                Handheld table order taker for floor waiters.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Panel 4: Snack Center Owner on Mobile */}
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            height: { xs: '200px', md: '100%' },
                            backgroundImage: 'linear-gradient(to bottom, rgba(0,111,66,0.2), rgba(0,111,66,0.9)), url("/snack_center_mobile_billing.png")',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transform: { xs: 'none', md: 'skewX(-6deg)' },
                            marginLeft: { xs: 0, md: '-10px' },
                            marginRight: { xs: 0, md: '-10px' },
                            transition: 'all 0.4s ease',
                            overflow: 'hidden',
                            '&:hover': {
                                flex: { xs: 1, md: 1.3 }
                            }
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                left: { xs: 20, md: 35 },
                                right: 20,
                                transform: { xs: 'none', md: 'skewX(6deg)' }
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 900, color: 'white' }}>
                                Snack Corner QSR
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', mt: 0.5, fontWeight: 600 }}>
                                Rapid mobile billing for quick service counters.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Dynamic Content: Portal Access */}
            <Box sx={{ maxWidth: 600, mx: 'auto', p: { xs: 2, md: 4 }, width: '100%' }}>
                <Card sx={{ borderRadius: 4, p: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <CardContent sx={{ textAlign: 'center', p: 0 }}>
                        <StorefrontIcon sx={{ fontSize: 50, color: '#008751', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#0f172a' }}>
                            Access Your POS
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                            तुमच्या रेस्टॉरंटचे नाव (subdomain) टाकून थेट बिलिंग स्क्रीन सुरु करा.
                        </Typography>

                        <form onSubmit={handleSearchSubmit}>
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                                <TextField
                                    required
                                    fullWidth
                                    placeholder="उदा. swad"
                                    value={searchSubdomain}
                                    onChange={(e) => setSearchSubdomain(e.target.value)}
                                    size="small"
                                    inputProps={{
                                        autoComplete: 'off',
                                        autoCorrect: 'off',
                                        autoCapitalize: 'none',
                                        spellCheck: 'false',
                                        inputMode: 'text',
                                        style: { touchAction: 'manipulation' }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700, pr: 1 }}>
                                                .dinexa.in
                                            </Typography>
                                        )
                                    }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    sx={{ height: 40, px: 3, fontWeight: 800, minWidth: 80 }}
                                >
                                    प्रवेश
                                </Button>
                            </Box>
                            {searchError && (
                                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                                    {searchError}
                                </Alert>
                            )}
                        </form>

                        <Divider sx={{ my: 2.5 }} />

                        <Button
                            variant="text"
                            onClick={() => setSignupOpen(true)}
                            sx={{ fontWeight: 800, color: '#008751', textTransform: 'none', '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' } }}
                        >
                            नवीन स्टोअर नोंदणीसाठी (Register Store) इथे क्लिक करा
                        </Button>
                    </CardContent>
                </Card>
            </Box>

            {/* Quick Contact Sales Bar */}
            <Box sx={{ bgcolor: '#ecfdf5', borderY: '1px solid #d1fae5', py: 4, px: 2, textAlign: 'center', mt: 'auto' }}>
                <Box sx={{ maxWidth: 800, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PhoneInTalkIcon sx={{ color: '#008751', fontSize: 32 }} />
                        <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2" sx={{ color: '#065f46', fontWeight: 800 }}>
                                To discuss for customized plan, you can call directly on:
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#047857', fontWeight: 900 }}>
                                +91 99219 46393
                            </Typography>
                        </Box>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RoomServiceIcon sx={{ color: '#008751' }} />
                        <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>
                            तुमच्या गरजेनुसार फिचर्स आणि अनुकूल (Custom) प्लॅन्स उपलब्ध!
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ borderTop: '1px solid #e2e8f0', py: 3, px: 4, bgcolor: '#ffffff', textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1, fontWeight: 500 }}>
                    © {new Date().getFullYear()} DinexaPOS Cloud SaaS Platform : Designed & Developed by e.Sense InfoSolutions. All rights reserved.
                </Typography>
            </Box>

            {/* Registration Dialog */}
            <Dialog
                open={signupOpen}
                onClose={() => setSignupOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RocketLaunchIcon color="primary" /> Register New POS Store
                    </Box>
                    <IconButton onClick={() => setSignupOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 3 }}>
                    {signupResult && (
                        <Box sx={{ mb: 3 }}>
                            <Alert severity={signupResult.success ? 'success' : 'error'} sx={{ borderRadius: 2 }}>
                                <Typography sx={{ fontWeight: 800 }}>{signupResult.message}</Typography>
                                {signupResult.success && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        sx={{ mt: 1.5, fontWeight: 800 }}
                                        onClick={() => {
                                            setSignupOpen(false);
                                            onSelectTenant(signupResult.subdomain);
                                        }}
                                    >
                                        Go to Login Terminal
                                    </Button>
                                )}
                            </Alert>
                        </Box>
                    )}

                    {!(signupResult && signupResult.success) && (
                        <form onSubmit={handleSignupSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        size="small"
                                        label="Restaurant Name"
                                        placeholder="e.g. Swad Maharashtra"
                                        value={signupForm.name}
                                        onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        size="small"
                                        label="Desired Subdomain"
                                        placeholder="e.g. swad"
                                        value={signupForm.subdomain}
                                        onChange={(e) => setSignupForm({ ...signupForm, subdomain: e.target.value })}
                                        helperText="Used as: swad.dinexa.in"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        size="small"
                                        label="Owner Full Name"
                                        value={signupForm.owner_name}
                                        onChange={(e) => setSignupForm({ ...signupForm, owner_name: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        size="small"
                                        type="email"
                                        label="Owner Email"
                                        placeholder="e.g. owner@example.com"
                                        value={signupForm.owner_email}
                                        onChange={(e) => setSignupForm({ ...signupForm, owner_email: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        required
                                        fullWidth
                                        size="small"
                                        type="password"
                                        label="Owner Password"
                                        value={signupForm.owner_password}
                                        onChange={(e) => setSignupForm({ ...signupForm, owner_password: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={signupLoading}
                                        sx={{ fontWeight: 800, height: 44, mt: 1 }}
                                    > {signupLoading ? 'Setting up Database, please wait...' : 'Create My Store Instance'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}